import { cn } from '@/shared/utils';
import { Description, FieldError, Select as HeroSelect, Label, ListBox } from '@heroui/react';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Placement } from 'react-aria-components';

const LIST_KEYS = new Set(['Escape', 'ArrowUp', 'ArrowDown', 'Enter', 'Tab']);

export interface Option {
  label: string;
  value: string | number;
  icon?: React.ComponentType<{ className?: string }>;
  isDisabled?: boolean;
  badge?: string;
}

export interface SelectClassNames {
  container?: string;
  select?: string;
  label?: string;
  trigger?: string;
  value?: string;
  popover?: string;
  listBox?: string;
  error?: string;
  description?: string;
}

export interface SelectProps {
  value: string | number;
  onChange: (value: string) => void;
  label?: string;
  options: readonly Option[];
  placeholder?: string;
  errorMessage?: string | undefined;
  description?: string | undefined;
  classNames?: SelectClassNames | undefined;
  hasError?: boolean | undefined;
  isOpen?: boolean | undefined;
  onOpenChange?: ((isOpen: boolean) => void) | undefined;
  isDisabled?: boolean | undefined;
  hideLabel?: boolean | undefined;
  descriptionAbove?: boolean | undefined;
  placement?: Placement;
  shouldFlip?: boolean;
  isSearchable?: boolean | undefined;
  searchPlaceholder?: string | undefined;
}

export function Select({
  value,
  onChange,
  label,
  options,
  placeholder,
  errorMessage,
  description,
  classNames,
  hasError,
  isOpen,
  onOpenChange,
  isDisabled,
  hideLabel,
  descriptionAbove,
  placement = 'bottom',
  shouldFlip,
  isSearchable,
  searchPlaceholder,
}: Readonly<SelectProps>) {
  const normalizedValue = value ? String(value) : null;

  const isOpenControlled = isOpen !== undefined && onOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const openValue = isOpenControlled ? Boolean(isOpen) : internalOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) setQuery('');
      if (isOpenControlled) {
        onOpenChange?.(nextOpen);
      } else {
        setInternalOpen(nextOpen);
      }
    },
    [isOpenControlled, onOpenChange],
  );

  const focusOnMount = useCallback((node: HTMLInputElement | null) => node?.focus(), []);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!LIST_KEYS.has(e.key)) e.stopPropagation();
  }, []);

  const handleChange = useCallback(
    (key: React.Key | null) => {
      if (key !== null) {
        onChange(String(key));
      }
      handleOpenChange(false);
    },
    [onChange, handleOpenChange],
  );

  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!isSearchable || !needle) return options;
    return options.filter((option) => String(option.label).toLowerCase().includes(needle));
  }, [options, query, isSearchable]);

  return (
    <div className={classNames?.container ?? ''}>
      <HeroSelect
        aria-label={label || 'Select'}
        placeholder={placeholder || 'Select an option'}
        selectedKey={normalizedValue}
        onSelectionChange={handleChange}
        isOpen={openValue}
        onOpenChange={handleOpenChange}
        isDisabled={isDisabled ?? false}
        isInvalid={hasError ?? false}
        className={cn('w-full text-xs sm:text-sm', classNames?.select)}
        variant="primary"
      >
        {label && !hideLabel && (
          <Label className={cn('block text-xs sm:text-sm', classNames?.label)}>{label}</Label>
        )}
        {descriptionAbove && description && !hasError && (
          <Description className={cn('mb-1 block', classNames?.description)}>
            {description}
          </Description>
        )}
        <HeroSelect.Trigger
          className={cn(
            'w-full h-8 sm:h-9 border border-input rounded-lg px-3 bg-card text-foreground',
            'flex flex-row items-center gap-2',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all',
            'data-placeholder:text-muted-foreground text-body-xs font-normal leading-normal',
            hasError && 'border-destructive focus:ring-destructive/30 focus:border-destructive',
            classNames?.trigger,
          )}
        >
          <HeroSelect.Value
            className={cn(
              'block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-body-xs',
              classNames?.value,
            )}
          />
          <HeroSelect.Indicator />
        </HeroSelect.Trigger>
        {!descriptionAbove && description && !hasError && (
          <Description className={classNames?.description ?? ''}>{description}</Description>
        )}
        {hasError && errorMessage && (
          <FieldError className={classNames?.error ?? ''}>{errorMessage}</FieldError>
        )}
        <HeroSelect.Popover
          placement={placement}
          {...(shouldFlip !== undefined && { shouldFlip })}
          maxHeight={200}
          className={cn('rounded-lg', classNames?.popover)}
        >
          {isSearchable && (
            <input
              type="text"
              value={query}
              ref={focusOnMount}
              placeholder={searchPlaceholder}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full h-8 px-2 mb-1 border-b border-input bg-card text-foreground text-body-xs focus:outline-none"
            />
          )}
          <ListBox className={cn('text-body-xs', classNames?.listBox)}>
            {visibleOptions.map((option) => (
              <ListBox.Item
                key={String(option.value)}
                id={String(option.value)}
                textValue={option.label}
                isDisabled={option.isDisabled ?? false}
                className="rounded-md"
              >
                <ListBox.ItemIndicator />
                <span className="flex items-center gap-2 text-body-xs">
                  {option.icon && (
                    <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                      <option.icon className="w-4 h-4" />
                    </span>
                  )}
                  <span>{option.label}</span>
                  {option.badge && (
                    <span className="ml-auto text-2xs px-1 py-px rounded bg-surface-secondary text-muted-foreground font-medium leading-tight">
                      {option.badge}
                    </span>
                  )}
                </span>
              </ListBox.Item>
            ))}
          </ListBox>
        </HeroSelect.Popover>
      </HeroSelect>
    </div>
  );
}
